import { isEmpty, without } from "lodash";

import { formatE164 } from "helpers";
import { TypeOfLocationValues } from "types";

const getCountry = (countries, appCountry) => {
	return countries?.find(item => item.code === appCountry);
};

const getTypeOfLocation = typeOfLocation => {
	const typeOfLocationResult = TypeOfLocationValues.getByName(typeOfLocation);
	return {
		id: typeOfLocationResult?.value,
		name: typeOfLocationResult?.displayName,
		value: typeOfLocationResult?.name,
	};
};

export const getApplicationContactInfoMapper = (contactInfo, countries) => {
	return {
		business_info_contact: {
			business_phone: formatE164(contactInfo?.business_phone) || "",
			business_email: contactInfo?.business_email,
			type_of_business: contactInfo?.type_of_business,
		},
		business_address: {
			address_line1: contactInfo?.business_address?.address_line1,
			address_line2: contactInfo?.business_address?.address_line2,
			city: contactInfo?.business_address?.city,
			country: getCountry(
				countries,
				contactInfo?.business_address?.country_code,
			),
			region_code: contactInfo?.business_address?.region_code,
			postal_code: contactInfo?.business_address?.postal_code,
			type_of_location: getTypeOfLocation(
				contactInfo?.business_address?.type_of_location,
			),
			location_if_other: contactInfo?.business_address?.location_if_other,
		},
		legal_address: {
			is_legal_address: String(contactInfo?.is_legal_address_the_same),
			address_line1: contactInfo?.legal_address?.address_line1,
			address_line2: contactInfo?.legal_address?.address_line2,
			city: contactInfo?.legal_address?.city,
			country: getCountry(
				countries,
				contactInfo?.legal_address?.country_code,
			),
			region_code: contactInfo?.legal_address?.region_code,
			postal_code: contactInfo?.legal_address?.postal_code,
			type_of_location: contactInfo?.legal_address?.type_of_location,
		},
		owners: without(
			[
				contactInfo?.owner_1,
				contactInfo?.owner_2,
				contactInfo?.owner_3,
				contactInfo?.owner_4,
			],
			null,
		).map(owner => {
			return {
				city: owner?.address?.city,
				address_line1: owner?.address?.address_line1,
				address_line2: owner?.address?.address_line2,
				email: owner?.email,
				phone: formatE164(owner?.phone) || "",
				postal_code: owner?.address?.postal_code,
				region_code: owner?.address?.region_code,
				country: getCountry(countries, owner?.address?.country_code),
			};
		}),
		is_primary_contact_the_same: contactInfo?.is_primary_contact_the_same,
		primary_info: {
			email: contactInfo?.primary_contact?.email,
			phone: formatE164(contactInfo?.primary_contact?.phone) || "",
		},
	};
};

export const updateApplicationContactInfoMapper = contactInfo => {
	return {
		business_phone: contactInfo?.business_info_contact?.business_phone,
		business_email: contactInfo?.business_info_contact?.business_email,
		business_address: {
			address_line1: contactInfo?.business_address?.address_line1,
			address_line2: contactInfo?.business_address?.address_line2,
			city: contactInfo?.business_address?.city,
			country: contactInfo?.business_address?.country?.name,
			country_code: contactInfo?.business_address?.country?.code,
			region_code: contactInfo?.business_address?.region_code,
			postal_code: contactInfo?.business_address?.postal_code,
			type_of_location:
				contactInfo?.business_address?.type_of_location?.value,
			location_if_other: contactInfo?.business_address?.location_if_other,
		},
		is_legal_address_the_same: JSON.parse(
			contactInfo?.legal_address?.is_legal_address,
		),
		legal_address: JSON.parse(contactInfo?.legal_address?.is_legal_address)
			? {}
			: {
					address_line1: contactInfo?.legal_address?.address_line1,
					address_line2: contactInfo?.legal_address?.address_line2,
					city: contactInfo?.legal_address?.city,
					country: contactInfo?.legal_address?.country?.name,
					country_code: contactInfo?.legal_address?.country?.code,
					region_code: contactInfo?.legal_address?.region_code,
					postal_code: contactInfo?.legal_address?.postal_code,
					type_of_location:
						contactInfo?.legal_address?.type_of_location,
			  },
		owner_1: {
			email: contactInfo?.owners[0]?.email,
			phone: contactInfo?.owners[0]?.phone?.split(" ").join(""),
			address: {
				country_code: contactInfo?.owners[0]?.country?.code,
				country: contactInfo?.owners[0]?.country?.name,
				city: contactInfo?.owners[0]?.city,
				address_line1: contactInfo?.owners[0]?.address_line1,
				address_line2: contactInfo?.owners[0]?.address_line2,
				postal_code: contactInfo?.owners[0]?.postal_code,
				region_code: contactInfo?.owners[0]?.region_code,
			},
		},
		owner_2: isEmpty(contactInfo?.owners[1])
			? null
			: {
					email: contactInfo?.owners[1]?.email,
					phone: contactInfo?.owners[1]?.phone,
					address: {
						country_code: contactInfo?.owners[1]?.country?.code,
						country: contactInfo?.owners[1]?.country?.name,
						city: contactInfo?.owners[1]?.city,
						address_line1: contactInfo?.owners[1]?.address_line1,
						address_line2: contactInfo?.owners[1]?.address_line2,
						postal_code: contactInfo?.owners[1]?.postal_code,
						region_code: contactInfo?.owners[1]?.region_code,
					},
			  },
		owner_3: isEmpty(contactInfo?.owners[2])
			? null
			: {
					email: contactInfo?.owners[2]?.email,
					phone: contactInfo?.owners[2]?.phone,
					address: {
						country_code: contactInfo?.owners[2]?.country?.code,
						country: contactInfo?.owners[2]?.country?.name,
						city: contactInfo?.owners[2]?.city,
						address_line1: contactInfo?.owners[2]?.address_line1,
						address_line2: contactInfo?.owners[2]?.address_line2,
						postal_code: contactInfo?.owners[2]?.postal_code,
						region_code: contactInfo?.owners[2]?.region_code,
					},
			  },
		owner_4: isEmpty(contactInfo?.owners[3])
			? null
			: {
					email: contactInfo?.owners[3]?.email,
					phone: contactInfo?.owners[3]?.phone,
					address: {
						country_code: contactInfo?.owners[3]?.country?.code,
						country: contactInfo?.owners[3]?.country?.name,
						city: contactInfo?.owners[3]?.city,
						address_line1: contactInfo?.owners[3]?.address_line1,
						address_line2: contactInfo?.owners[3]?.address_line2,
						postal_code: contactInfo?.owners[3]?.postal_code,
						region_code: contactInfo?.owners[3]?.region_code,
					},
			  },
		primary_contact: {
			email: contactInfo?.primary_info?.email,
			phone: contactInfo?.primary_info?.phone,
		},
	};
};
